import {normFamily, normSize, cssFamily} from '../constants';

const ALLOWED_TAGS = new Set([
  'section', 'div', 'p', 'h1', 'h2', 'h3', 'strong', 'em', 'u', 'span', 'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'br', 'img',
]);
const DROP_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'noscript', 'template',
  'svg', 'form', 'input', 'button', 'textarea', 'select', 'video', 'audio', 'canvas', 'head', 'title',
]);
const ALLOWED_CLASSES = new Set(['page', 'room-details', 'seal']);
const RENAME = { b: 'strong', i: 'em' };
const TEXT_ALIGN_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'li', 'td', 'th']);
const STRUCTURAL_PARENTS = new Set(['SECTION', 'UL', 'OL', 'TABLE', 'TBODY', 'THEAD', 'TR', 'BODY', 'DIV']);
const IMG_SRC = /^(data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+|\{\{(logo|seal)\}\})$/;

function cleanStyle(raw, tag) {
  const out = [];
  String(raw || '').split(';').forEach((decl) => {
    const i = decl.indexOf(':');
    if (i < 0) return;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const val = decl.slice(i + 1).trim().toLowerCase();
    if (prop === 'text-align' && TEXT_ALIGN_TAGS.has(tag) && /^(left|center|right)$/.test(val)) {
      out.push(`text-align: ${val}`);
    } else if (prop === 'padding-inline-start' && tag !== 'li' && /^\d+px$/.test(val)) {
      out.push(`padding-inline-start: ${val}`);
    }
  });
  return out.join('; ');
}

function fontStyle(raw) {
  let fam = null;
  let size = null;
  String(raw || '').split(';').forEach((d) => {
    const i = d.indexOf(':');
    if (i < 0) return;
    const p = d.slice(0, i).trim().toLowerCase();
    const v = d.slice(i + 1).trim();
    if (p === 'font-family') fam = normFamily(v);
    else if (p === 'font-size') size = normSize(v);
  });
  const out = [];
  if (fam) out.push(`font-family: ${cssFamily(fam)}`);
  if (size) out.push(`font-size: ${size}`);
  return out.join('; ');
}

function cleanNode(node, doc) {
  if (node.nodeType === 3) {
    const v = node.nodeValue;
    const parent = node.parentNode && node.parentNode.nodeName;
    if (!v.trim() && parent && STRUCTURAL_PARENTS.has(parent)) return null;
    return doc.createTextNode(v);
  }
  if (node.nodeType !== 1) return null;
  let tag = node.tagName.toLowerCase();
  if (DROP_TAGS.has(tag)) return null;
  tag = RENAME[tag] || tag;
  if (tag === 'div' && node.classList.contains('doc-header')) return null;

  const kids = () => {
    const frag = doc.createDocumentFragment();
    node.childNodes.forEach((c) => {
      const r = cleanNode(c, doc);
      if (r) frag.appendChild(r);
    });
    return frag;
  };
  if (!ALLOWED_TAGS.has(tag)) return kids();

  const inline = tag === 'strong' || tag === 'em' || tag === 'u' || tag === 'span';
  if (inline) {
    const fs = fontStyle(node.getAttribute('style'));
    if (tag === 'span') {
      if (!fs) return kids();
      const sp = doc.createElement('span');
      sp.setAttribute('style', fs);
      sp.appendChild(kids());
      return sp;
    }
    const el = doc.createElement(tag);
    if (fs) {
      const sp = doc.createElement('span');
      sp.setAttribute('style', fs);
      sp.appendChild(kids());
      el.appendChild(sp);
    } else {
      el.appendChild(kids());
    }
    return el;
  }

  const el = doc.createElement(tag);
  const cls = (node.getAttribute('class') || '').split(/\s+/).filter((c) => ALLOWED_CLASSES.has(c));
  if (cls.length) el.setAttribute('class', cls.join(' '));
  if (tag === 'div' && !cls.length) return kids();

  const style = cleanStyle(node.getAttribute('style'), tag);
  if (style) el.setAttribute('style', style);

  if (tag === 'img') {
    const src = (node.getAttribute('src') || '').trim();
    if (!IMG_SRC.test(src)) return null;
    el.setAttribute('src', src);
    el.setAttribute('alt', node.getAttribute('alt') || '');
    return el;
  }
  if (tag === 'hr' || tag === 'br') return el;
  el.appendChild(kids());
  return el;
}

const isEmptyP = (el) =>
  el && el.tagName === 'P' && !el.textContent.replace(/\u00a0/g, ' ').trim() && !el.querySelector('img');

function cleanupSave(out) {
  const walker = document.createTreeWalker(out, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((t) => { t.nodeValue = t.nodeValue.replace(/\u00a0/g, ' '); });

  out.querySelectorAll('br').forEach((br) => {
    const prev = br.previousSibling;
    if (prev && prev.nodeName === 'BR') br.remove();
  });
  out.querySelectorAll('p, h1, h2, h3, li').forEach((el) => {
    while (el.lastChild && el.lastChild.nodeName === 'BR' && el.textContent.trim()) el.lastChild.remove();
  });
  out.querySelectorAll('section.page').forEach((page) => {
    while (page.children.length > 1 && isEmptyP(page.lastElementChild)) page.lastElementChild.remove();
  });
  out.querySelectorAll('section.page').forEach((page) => {
    const blank = !page.textContent.trim() && !page.querySelector('img, table, hr, .room-details, .seal');
    if (blank && out.querySelectorAll('section.page').length > 1) page.remove();
  });
}

function cleanupLoad(out) {
  out.querySelectorAll('p, h1, h2, h3').forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector('img')) el.textContent = '';
  });
}

/** Whitelist sanitizer. mode: 'save' (also tidies) or 'load'. Returns an HTML string. */
export function sanitize(html, mode = 'save') {
  const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
  const out = document.createElement('div');
  doc.body.childNodes.forEach((c) => {
    const r = cleanNode(c, document);
    if (r) out.appendChild(r);
  });
  out.querySelectorAll('strong strong, em em, u u').forEach((inner) => {
    const p = inner.parentNode;
    while (inner.firstChild) p.insertBefore(inner.firstChild, inner);
    inner.remove();
  });
  out.querySelectorAll('strong, em, u, span').forEach((el) => {
    if (!el.textContent && !el.querySelector('img')) el.remove();
  });
  if (mode === 'save') cleanupSave(out); else cleanupLoad(out);
  return out.innerHTML;
}